import { Dispatch, SetStateAction } from "react";
import { t } from "i18next";

import { RoleAssignmentUpdates, SecurityGroupRoleAssignmentsReadModel } from "../../../../../../@api";

import { translationKeys } from "../../../../../models/app-translation-keys";
import {
  ISharedDialogAction,
  ISharedDialogActionContext,
  sharedDialog,
} from "../../../../Shared/Components/Dialogs/SharedDialog";
import {
  createRoleAssignmentRequest,
  IFormValuesWithSecurityGroup,
  IManageSecurityGroupDialogState,
} from "./manageSecurityGroupDialogTypes";

interface IManageSecurityGroupDialogFooterActionsProps {
  data?: SecurityGroupRoleAssignmentsReadModel;
  onHide: (addedSecurityGroup?: SecurityGroupRoleAssignmentsReadModel) => void;
  isEdit: boolean;
  updateRoleAssignments: (requestBody: { [key: string]: RoleAssignmentUpdates }) => Promise<unknown>;
}

const getFormStatus = (state: IManageSecurityGroupDialogState) => {
  const isDisabled = !state.isValid || state.isUpdating || !state.hasPendingChanges;

  const tooltip =
    state.isValid || state.isUpdating
      ? undefined
      : t(state.hasPendingChanges ? translationKeys.form.formHasNoChangesError : translationKeys.form.formInvalidError);

  return {
    isDisabled,
    tooltip,
  };
};

export const manageSecurityGroupDialogFooterActions = ({
  data,
  onHide,
  isEdit,
  updateRoleAssignments,
}: IManageSecurityGroupDialogFooterActionsProps) => {
  return ({ id, sharedState, setSharedDialogState }: ISharedDialogActionContext): ISharedDialogAction[] => {
    const dialogState = sharedState as IManageSecurityGroupDialogState;
    const setState = setSharedDialogState as Dispatch<SetStateAction<IManageSecurityGroupDialogState>>;

    const setIsUpdating = (value: boolean) => {
      setState(prev => ({ ...prev, isUpdating: value }));
    };

    const formStatus = getFormStatus(dialogState);

    const handleSubmit = async () => {
      const formData = dialogState.formData;

      if (!formData?.securityGroup) {
        return;
      }

      const typed = formData as IFormValuesWithSecurityGroup;

      setIsUpdating(true);

      try {
        await updateRoleAssignments(createRoleAssignmentRequest(typed, data));
      } catch {
        setIsUpdating(false);
        return;
      }

      const result: SecurityGroupRoleAssignmentsReadModel = {
        securityGroup: typed.securityGroup,
        assignedRoleIds: typed.assignedRoleIds,
      };

      sharedDialog.closeSharedDialog(id);
      onHide(result);
    };

    return [
      {
        label: t(translationKeys.actions.cancel),
        isTextButton: true,
        isDisabled: false,
        isLoading: false,
        tooltip: undefined,
        tooltipOptions: undefined,
        onClick: () => {
          sharedDialog.closeSharedDialog(id);
          onHide();
        },
      },

      {
        label: t(isEdit ? translationKeys.actions.apply : translationKeys.actions.add),
        isTextButton: false,
        isDisabled: formStatus.isDisabled,
        isLoading: dialogState.isUpdating,
        tooltip: formStatus.tooltip,
        tooltipOptions: {
          position: "top",
          showOnDisabled: true,
          showDelay: 200,
        },
        onClick: handleSubmit,
      },
    ];
  };
};
